import { CommonModule } from '@angular/common';
import { Component, computed, effect, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { ChatComponent, HistoryComponent, SplitPaneComponent, SplitPaneVerticalComponent } from '@components';
import { PhoneCallRecord } from '@models';
import { PhoneCallRecordService, ToastService, WhatsappIntegrationService, WhatsappProjectMessage } from '@services';
import { WorkspaceFacade, UserFacade } from '@state';

type CallsScope = 'org' | 'project' | 'me' | 'all';
type WhatsappView = 'all' | 'messages' | 'calls';

@Component({
  selector: 'app-whatsapp',
  templateUrl: './whatsapp.page.html',
  styleUrls: ['./whatsapp.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ChatComponent,
    HistoryComponent,
    SplitPaneComponent,
    SplitPaneVerticalComponent,
  ],
})
export class WhatsappPage {
  private userStore = inject(UserFacade);
  loadingCalls = signal(false);
  loadingMessages = signal(false);
  loadingLinkStatus = signal(false);
  sendingOtp = signal(false);
  verifyingOtp = signal(false);
  unlinkingPhone = signal(false);

  scope = signal<CallsScope>('org');
  search = signal('');
  view = signal<WhatsappView>('all');
  linkedPhone = signal<string | null>(null);
  hasProjectPhoneMapping = signal(false);
  otpExpiresAt = signal<number | null>(null);
  phoneInput = signal('');
  otpCodeInput = signal('');

  callRecords = signal<PhoneCallRecord[]>([]);
  messageHistory = signal<WhatsappProjectMessage[]>([]);

  private currentUserId = computed(() => this.userStore.current()?.id ?? '');
  private activeProjectId = computed(() => this.store.activeProjectId());
  private activeOrganizationId = computed(() => this.store.selectedOrganizationId() ?? null);

  filteredCalls = computed(() => {
    const q = (this.search() || '').toLowerCase().trim();
    const items = this.callRecords();
    if (!q) return items;
    return items.filter((r) =>
      [r.summary, r.from, r.to, r.channel, r.direction, r.status, r.projectId, r.organizationId, r.userId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  });

  filteredMessages = computed(() => {
    const q = (this.search() || '').toLowerCase().trim();
    const items = this.messageHistory();
    if (!q) return items;
    return items.filter((m) =>
      [m.text, m.phoneNumber, m.direction, m.status, m.providerMessageId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  });

  public whatsappSuggestions: any[] = [
    { icon: 'bi bi-link-45deg', prompt: 'How do I connect WhatsApp to this project?' },
    { icon: 'bi bi-chat-dots', prompt: 'Draft a product update to send via WhatsApp' },
    { icon: 'bi bi-lightning', prompt: 'What WhatsApp automation can we add first?' },
  ];

  public whatsappContext: any = {
    page: 'whatsapp',
    purpose: 'Manage project WhatsApp integration and chat-based feature requests',
    integrationStatus: 'not_connected',
    activeAgents: [],
    recentRequests: [],
    outputs: ['Integration status', 'Agent responses', 'Requested updates'],
  };

  constructor(
    private store: WorkspaceFacade,
    private callApi: PhoneCallRecordService,
    private whatsappApi: WhatsappIntegrationService,
    private toast: ToastService
  ) {
    effect(() => {
      const userId = this.currentUserId();
      const projectId = this.activeProjectId();
      if (!userId || !projectId) return;
      this.phoneInput.set('');
      this.otpCodeInput.set('');
      void this.refreshCalls();
      void this.refreshIntegrationStatus();
      void this.refreshMessages();
    });
  }

  async refreshCalls(): Promise<void> {
    const userId = this.currentUserId();
    if (!userId) return;
    this.loadingCalls.set(true);
    try {
      const scope = this.scope();
      const where: any = {};
      if (scope === 'me') where.userId = userId;
      if (scope === 'project') where.projectId = this.activeProjectId() ?? undefined;
      if (scope === 'org') where.organizationId = this.activeOrganizationId() ?? undefined;

      const res = await this.callApi.find(Object.keys(where).length ? { where } : undefined);
      const items = (res.data ?? []) as PhoneCallRecord[];
      items.sort((a: any, b: any) => ((b.startedAt ?? 0) - (a.startedAt ?? 0)));
      this.callRecords.set(items);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load call records', 'WhatsApp');
      this.callRecords.set([]);
    } finally {
      this.loadingCalls.set(false);
    }
  }

  async refreshIntegrationStatus(): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    this.loadingLinkStatus.set(true);
    try {
      const status = await this.whatsappApi.integrationStatus(projectId);
      this.hasProjectPhoneMapping.set(!!status.hasProjectPhoneMapping);
      this.linkedPhone.set(status.linkedPhoneNumber ?? null);
      this.whatsappContext = {
        ...this.whatsappContext,
        integrationStatus: status.isLinked ? 'connected' : status.hasProjectPhoneMapping ? 'pending_link' : 'not_configured',
      };
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load WhatsApp integration status', 'WhatsApp');
      this.hasProjectPhoneMapping.set(false);
      this.linkedPhone.set(null);
    } finally {
      this.loadingLinkStatus.set(false);
    }
  }

  async requestOtp(): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    const phone = (this.phoneInput() || '').trim();
    if (!phone) {
      this.toast.warning('Enter your cell number first', 'WhatsApp');
      return;
    }
    this.sendingOtp.set(true);
    try {
      const res = await this.whatsappApi.requestOtp({
        projectId,
        organizationId: this.activeOrganizationId() ?? undefined,
        phoneNumber: phone,
      });
      this.otpExpiresAt.set(res.expiresAt ?? null);
      this.toast.success('OTP sent to your WhatsApp number', 'WhatsApp');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to send OTP', 'WhatsApp');
    } finally {
      this.sendingOtp.set(false);
    }
  }

  async verifyOtp(): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    const phone = (this.phoneInput() || '').trim();
    const code = (this.otpCodeInput() || '').trim();
    if (!phone || !code) {
      this.toast.warning('Enter phone number and OTP code', 'WhatsApp');
      return;
    }
    this.verifyingOtp.set(true);
    try {
      await this.whatsappApi.verifyOtp({
        projectId,
        organizationId: this.activeOrganizationId() ?? undefined,
        phoneNumber: phone,
        code,
      });
      this.toast.success('Phone linked for this project', 'WhatsApp');
      this.otpCodeInput.set('');
      await this.refreshIntegrationStatus();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'OTP verification failed', 'WhatsApp');
    } finally {
      this.verifyingOtp.set(false);
    }
  }

  async unlinkPhone(): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    this.unlinkingPhone.set(true);
    try {
      await this.whatsappApi.unlink(projectId);
      this.toast.success('Phone unlinked from project', 'WhatsApp');
      this.linkedPhone.set(null);
      this.otpCodeInput.set('');
      await this.refreshIntegrationStatus();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to unlink phone', 'WhatsApp');
    } finally {
      this.unlinkingPhone.set(false);
    }
  }

  async refreshMessages(): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    this.loadingMessages.set(true);
    try {
      const items = await this.whatsappApi.listMessages(projectId, 150);
      items.sort((a, b) => Number(b.timestamp ?? 0) - Number(a.timestamp ?? 0));
      this.messageHistory.set(items);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load WhatsApp messages', 'WhatsApp');
      this.messageHistory.set([]);
    } finally {
      this.loadingMessages.set(false);
    }
  }
}
